import React, { useState, useContext, useEffect } from 'react';

import { Button as MuiButton, Divider, Grow, Paper, TextField } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';

import { GlobalContext } from '../../store';
import { IAdminGroup } from '../../store/admin/groups/store';
import { IAdminMembership } from '../../store/admin/memberships/store';
import { IMessageNotification } from '../../store/notification/store';

import Button from '../../components/Button';
import Input from '../../components/Input';
import Tooltip from '../../components/Tooltip';
import { FormConsumer, FormProvider, IFormAttributes } from '../Forms';
import Modal from '../Modal';
import { useBoolean } from '../../scenes/common/Hooks/useBoolean.hook';
import { useRouteInfoState } from '../RouteInfo/Context/RouteInfo.context';

interface IManagePlayerGroupsProps {
  selectedMembers: IAdminMembership[];
}

const ManagePlayerGroups: React.FunctionComponent<IManagePlayerGroupsProps> = props => {
  const { selectedMembers } = props;

  const {
    actions: {
      adminGroups: { createGroup, updateGroupMembers, deleteGroup },
      notifications: { push: pushNotification }
    },
    adminGroups
  } = useContext(GlobalContext);

  const { clubId } = useRouteInfoState();

  const [addGroupEnabled, setAddGroupEnabled, toggleAddGroup] = useBoolean(false);

  const [modalIsOpen, , modalToggle] = useBoolean(false);

  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);

  const [groups, setGroups] = useState<IAdminGroup[]>([]);

  useEffect(() => {
    if (adminGroups.groups.length > 0) {
      setGroups(adminGroups.groups);
    }
  }, [adminGroups]);

  const onCreateGroup = async (data: any) => {
    if (clubId) {
      setAddGroupEnabled(false);

      const body = {
        description: data.description,
        name: data.name
      };

      const response = await createGroup(Number(clubId), body);
      if (response.errors) {
        await pushNotification(({
          status: 'unread',
          text: response.errors.groups.msg,
          type: 'error'
        } as unknown) as IMessageNotification);
      } else {
        await pushNotification(({
          status: 'unread',
          text: 'Club group created successfully',
          type: 'success'
        } as unknown) as IMessageNotification);
      }
    }
  };

  const onAddMembers = (group: IAdminGroup) => async () => {
    if (clubId) {
      const selectedMembersIds = selectedMembers.map((item: IAdminMembership) => item.id);

      const membersUnion = {
        members: Array.from(new Set([...group.members, ...selectedMembersIds]))
      };

      const response = await updateGroupMembers(Number(clubId), group.id, membersUnion);
      if (response.errors) {
        await pushNotification(({
          status: 'unread',
          text: response.errors.groups.msg,
          type: 'error'
        } as unknown) as IMessageNotification);
      } else {
        await pushNotification(({
          status: 'unread',
          text: 'Added members successfully',
          type: 'success'
        } as unknown) as IMessageNotification);
      }
    }
  };

  const onDeleteGroup = async () => {
    let response;

    if (selectedGroupId && clubId) {
      response = await deleteGroup(clubId, selectedGroupId);
    }

    if (response && response.errors) {
      await pushNotification(({
        status: 'unread',
        text: response.errors.groups.msg,
        type: 'error'
      } as unknown) as IMessageNotification);
    } else {
      await pushNotification(({
        status: 'unread',
        text: 'Group deleted successfully',
        type: 'success'
      } as unknown) as IMessageNotification);
    }
  };

  const setGroup = (groupId: number): void => {
    setSelectedGroupId(groupId);
  };

  const onRemoveGroup = async () => {
    await onDeleteGroup();
    modalToggle();
  };

  return (
    <Paper elevation={1} className="paper manage-player-groups">
      <div className="header">
        <h3 className="header-title">
          GROUPS
          <Tooltip sectionKey="PLAYER_GROUPS.GROUPS" classes={{ fab: 'card-tooltip-fab' }} />
        </h3>
      </div>

      <Divider />

      <div className="add-group-container">
        <MuiButton onClick={toggleAddGroup} className="add-group-button">
          <div className="icon-container">
            <AddIcon className="edit-card-icon" />
          </div>
          ADD NEW GROUP
        </MuiButton>

        {!!addGroupEnabled && (
          <Grow in={addGroupEnabled}>
            <div>
              <FormProvider
                defaultValues={{ description: '', name: '' }}
                onSubmit={onCreateGroup}
                validations={{
                  description: 'required',
                  name: 'required'
                }}
              >
                <FormConsumer>
                  {({ values, formActions: { setValue }, errors }: IFormAttributes) => (
                    <Paper elevation={4} className="paper add-new-group-container">
                      <div className="group-name">
                        <Input
                          label="GROUP NAME"
                          id="group-name-input"
                          type="text"
                          defaultValue={values.name}
                          onChange={setValue('name')}
                          hasError={errors.name}
                          errorMessage="You must provide a name"
                          fullWidth={true}
                        />
                      </div>

                      <div className="group-description">
                        <p>GROUP DESCRIPTION</p>

                        <TextField
                          defaultValue={values.description}
                          fullWidth={true}
                          multiline={true}
                          rows={2}
                          rowsMax={2}
                          error={errors.description}
                          variant="outlined"
                          onChange={setValue('description')}
                        />
                      </div>

                      <div className="buttons-container">
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={
                            errors.FORM || (values.name === '' || values.description === '')
                          }
                          color="primary"
                        >
                          ADD GROUP
                        </Button>

                        <Button variant="text" color="primary" onClick={toggleAddGroup}>
                          CANCEL
                        </Button>
                      </div>
                    </Paper>
                  )}
                </FormConsumer>
              </FormProvider>
            </div>
          </Grow>
        )}
      </div>

      <div className="groups-list-container">
        {groups.map((group: IAdminGroup) => (
          <Paper key={group.id} className="paper group-item">
            <div className="left">
              <div>
                <p className="group-title">{group.name}</p>

                <p className="group-members">{`${group.members.length} members`}</p>
              </div>

              <div>
                <p className="group-description">{group.description}</p>
              </div>
            </div>

            <div className="right">
              <Button
                disabled={selectedMembers.length === 0}
                onClick={() => onAddMembers(group)}
                variant="contained"
                color="primary"
              >
                Add Players
              </Button>

              <Button
                onClick={() => {
                  setGroup(group.id);
                  modalToggle();
                }}
                variant="text"
                color="primary"
              >
                Delete Group
              </Button>
            </div>
          </Paper>
        ))}
      </div>

      <Modal
        onClose={modalToggle}
        open={modalIsOpen}
        className="group-removal-modal"
        title="DELETE GROUP"
        wideButton={true}
        actions={[
          <Button
            key="button-remove"
            fullWidth={true}
            onClick={onRemoveGroup}
            variant="contained"
            className="confirm-removal-button"
            color="primary"
          >
            YES, REMOVE THIS GROUP FROM THE CLUB
          </Button>
        ]}
      >
        <p>Are you sure you want to remove this group?</p>
      </Modal>
    </Paper>
  );
};

export default ManagePlayerGroups;
